import type { ClientGame, ClientGameCtx } from "./index";
import { INV } from "../../shared/arcade";
import { ArcadeControls } from "../controls";
import { sound } from "../sound";
import { Fx } from "../fx";
import { sticker, roundRectPath } from "../draw";
import { C, TINT } from "../theme";
import {
  countdownOverlay, safeInsets, hudLayout, playfield, controlsReserve, boardSticker, BOARD_R,
} from "./asteroids";

const ROW_COLS = [C.coral, C.mango, C.mint, C.bubblegum];
const MINE = C.green;
const RIVAL = C.coral;

export class InvadersClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private controls = new ArcadeControls();
  private fx = new Fx();
  private raf = 0;
  private last = performance.now();
  private t = 0;
  private inputTimer = 0;
  private marchTimer = 0;
  private marchStep = 0;
  private prevAlive: boolean[] = [];
  private levelFlash = 0;
  private stars: { x: number; y: number; r: number }[] = [];

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();
    this.canvas = document.createElement("canvas");
    this.canvas.id = "game-canvas";
    ctx.root.appendChild(this.canvas);
    this.controls.mount(ctx.root, { actionLabel: "Fire", hint: "drag left/right to move" });
    for (let i = 0; i < 50; i++) this.stars.push({ x: Math.random(), y: Math.random(), r: 0.5 + Math.random() });

    ctx.room.onMessage("i-shoot", (m: any) => { if (m?.slot === ctx.mySlot) sound.laser(); });
    ctx.room.onMessage("i-kill", () => sound.invaderHit());
    ctx.room.onMessage("i-die", (m: any) => {
      sound.shipBoom();
      const s = this.ctx.room.state.invaders.ships?.[m?.slot ?? 0];
      if (s) this.fx.burst(s.x, INV.shipY + 4, { count: 22, speed: 0.13, colour: m?.slot === ctx.mySlot ? [MINE, TINT.mint] : [RIVAL, TINT.coral], life: 650, square: true });
      this.fx.shake(m?.slot === ctx.mySlot ? 9 : 5, 360);
      try { navigator.vibrate?.(m?.slot === ctx.mySlot ? [30, 40, 60] : 20); } catch { /* */ }
    });
    ctx.room.onMessage("i-level", () => { sound.levelUp(); this.levelFlash = 1; });

    this.inputTimer = window.setInterval(() => {
      if (this.ctx.room.state.phase !== "playing") return;
      this.ctx.room.send("input", { ax: this.controls.x, fire: this.controls.action });
    }, 33);

    const tickMarch = () => {
      const st = this.ctx.room.state.invaders;
      const alive = st.aliveMask ? [...st.aliveMask].filter(Boolean).length : 32;
      const total = INV.cols * INV.rows;
      if (this.ctx.room.state.phase === "playing" && alive > 0) sound.march(this.marchStep++);
      const delay = 220 + (alive / total) * 620;
      this.marchTimer = window.setTimeout(tickMarch, delay);
    };
    tickMarch();

    this.last = performance.now();
    this.loop();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
    clearInterval(this.inputTimer);
    clearTimeout(this.marchTimer);
    this.controls.unmount();
  }

  private loop = () => {
    const now = performance.now();
    const dt = Math.min(50, now - this.last);
    this.last = now;
    this.t += dt;
    try { this.draw(dt); } catch (e) { console.error("invaders frame", e); }
    this.raf = requestAnimationFrame(this.loop);
  };

  private draw(dt: number) {
    const { room, mySlot } = this.ctx;
    const c = this.canvas;
    const g = c.getContext("2d")!;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const cw = c.clientWidth, ch = c.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (c.width !== cw * dpr || c.height !== ch * dpr) { c.width = cw * dpr; c.height = ch * dpr; }
    g.save();
    g.scale(dpr, dpr);

    this.fx.update(dt);
    this.levelFlash = Math.max(0, this.levelFlash - dt * 0.002);
    const sh = this.fx.shakeXY();
    const st = room.state.invaders;
    const me = st.ships?.[mySlot];
    const rival = st.ships?.[mySlot === 0 ? 1 : 0];

    // HUD layout first so the playfield can start right under it
    const ins = safeInsets();
    const hud = st.solo
      ? hudLayout(g, cw, ins.top + 8, [
          { text: "♥".repeat(Math.max(0, me?.lives ?? 0)) || "–", ink: C.coral },
          { text: String(st.level ?? 1), label: "wave" },
          { text: String(me?.score ?? 0), label: "score" },
        ], room.state.mode === "solo")
      : hudLayout(g, cw, ins.top + 8, [
          { text: String(me?.score ?? 0), label: `you ♥${Math.max(0, me?.lives ?? 0)}`, fill: TINT.mint },
          { text: String(st.level ?? 1), label: "wave" },
          { text: String(rival?.score ?? 0), label: `rival ♥${Math.max(0, rival?.lives ?? 0)}`, fill: TINT.coral },
        ], false);

    // deep-night space, edge to edge
    g.fillStyle = C.nightDeep;
    g.fillRect(0, 0, cw, ch);

    const { scale, ox, oy } = playfield(cw, ch, hud.bottom + 10, ins.bottom + controlsReserve(cw, ch), INV.W, INV.H);
    const bw = INV.W * scale, bh = INV.H * scale;
    boardSticker(g, ox, oy, bw, bh, C.night);

    g.fillStyle = TINT.grape;
    g.globalAlpha = 0.5;
    for (const s of this.stars) { g.beginPath(); g.arc(s.x * cw, s.y * ch, s.r, 0, Math.PI * 2); g.fill(); }
    g.globalAlpha = 1;

    g.save();
    roundRectPath(g, ox, oy, bw, bh, BOARD_R);
    g.clip();
    g.translate(ox + sh.x, oy + sh.y);
    g.scale(scale, scale);

    const frame = Math.floor(this.t / 400) % 2;
    const bob = Math.sin(this.t / 380) * 1.5;

    // detect kills for particles
    for (let idx = 0; idx < INV.cols * INV.rows; idx++) {
      const alive = !!st.aliveMask?.[idx];
      if (this.prevAlive[idx] && !alive) {
        const rr = (idx / INV.cols) | 0, cc = idx % INV.cols;
        const x = INV.startX + cc * INV.spanX + (st.blockX ?? 0);
        const y = INV.startY + rr * INV.spanY + (st.blockY ?? 0);
        this.fx.burst(x, y, { count: 10, speed: 0.09, colour: [ROW_COLS[rr % 4]!, C.white], life: 400 });
      }
      this.prevAlive[idx] = alive;
    }

    // invaders — pre-rendered outlined sprites
    const k = scale * dpr;
    for (let r = 0; r < INV.rows; r++) {
      const spr = invaderSprite(ROW_COLS[r % 4]!, frame, k);
      for (let col = 0; col < INV.cols; col++) {
        if (!st.aliveMask?.[r * INV.cols + col]) continue;
        const x = INV.startX + col * INV.spanX + (st.blockX ?? 0);
        const y = INV.startY + r * INV.spanY + (st.blockY ?? 0) + bob;
        g.drawImage(spr, x - SPR_W / 2, y - SPR_H / 2, SPR_W, SPR_H);
      }
    }

    // ground line
    g.strokeStyle = "rgba(225,212,255,0.4)";
    g.lineWidth = 1.5 / scale;
    g.setLineDash([4, 6]);
    g.beginPath(); g.moveTo(10, INV.bottomLine + 18); g.lineTo(INV.W - 10, INV.bottomLine + 18); g.stroke();
    g.setLineDash([]);

    // ships — sticker cannon
    st.ships?.forEach((s: any, slot: number) => {
      if (!s.alive) return;
      if (s.respawn > 0 && Math.floor(s.respawn / 130) % 2 === 0) return;
      const col = slot === mySlot ? MINE : RIVAL;
      const w = INV.shipW;
      g.save();
      g.translate(s.x, INV.shipY);
      sticker(g, () => {
        g.beginPath();
        g.moveTo(-w / 2, INV.shipH);
        g.lineTo(-w / 2, INV.shipH * 0.4);
        g.lineTo(-w / 6, -2);
        g.lineTo(-2, -10);
        g.lineTo(2, -10);
        g.lineTo(w / 6, -2);
        g.lineTo(w / 2, INV.shipH * 0.4);
        g.lineTo(w / 2, INV.shipH);
        g.closePath();
      }, {
        fill: col, outlineW: 2.2 / scale, shadowOffset: 2.5 / scale,
        shadowColour: "rgba(16,10,40,0.7)", highlight: "rgba(255,255,255,0.6)",
      });
      g.restore();
    });

    // shots + bombs: coloured bars with an ink rim
    const rim = 1.1 / scale;
    st.shots?.forEach((s: any) => {
      const col = s.owner === mySlot ? MINE : RIVAL;
      this.fx.trail(s.x, s.y + 3, col, 1.6);
      g.fillStyle = C.ink;
      g.fillRect(s.x - 1.8 - rim, s.y - 7 - rim, 3.6 + rim * 2, 10 + rim * 2);
      g.fillStyle = col;
      g.fillRect(s.x - 1.8, s.y - 7, 3.6, 10);
    });
    st.bombs?.forEach((b: any) => {
      const zig = Math.sin(this.t / 60 + b.y) * 2;
      g.fillStyle = C.ink;
      g.fillRect(b.x - 2 + zig - rim, b.y - 5 - rim, 4 + rim * 2, 9 + rim * 2);
      g.fillStyle = C.mango;
      g.fillRect(b.x - 2 + zig, b.y - 5, 4, 9);
    });

    this.fx.draw(g);
    g.restore();

    if (this.levelFlash > 0) {
      g.fillStyle = `rgba(189,243,227,${0.35 * this.levelFlash})`;
      g.fillRect(0, 0, cw, ch);
    }
    hud.draw();
    if (room.state.phase === "countdown") countdownOverlay(g, cw, ch, room.state.countdown);
    g.restore();
  }

  results(ctx: ClientGameCtx) {
    const st = ctx.room.state.invaders;
    const me = st.ships?.[ctx.mySlot];
    const rival = st.ships?.[ctx.mySlot === 0 ? 1 : 0];
    const d = document.createElement("div");
    d.className = "arc-final";
    d.innerHTML = st.solo
      ? `<div class="score-big">${me?.score ?? 0}</div><div class="arc-note">survived to wave ${st.level}</div>`
      : `<div class="score-big">${me?.score ?? 0} : ${rival?.score ?? 0}</div><div class="arc-note">invader score</div>`;
    return d;
  }
}

// ---- invader sprites ----
// 8×6 pixel art at P world units per pixel, with an ink outline and a hard ink
// drop shadow baked in. Rendered once per (colour, frame, device scale) to an
// offscreen canvas — 32 invaders × ~34 outlined pixels per frame would
// otherwise be thousands of fillRects.

const P = 2.6;
const PAD = 2.4;                 // world units around the art for outline + shadow
const SPR_W = 8 * P + PAD * 2;
const SPR_H = 6 * P + PAD * 2;
const ART = [
  ["..#..#..", ".######.", "##.##.##", "########", "#.####.#", "#..##..#"],
  ["..#..#..", ".######.", "##.##.##", "########", ".#....#.", "#.#..#.#"],
];
const spriteCache = new Map<string, HTMLCanvasElement>();

function invaderSprite(col: string, frame: number, k: number): HTMLCanvasElement {
  const kq = Math.max(0.5, Math.round(k * 4) / 4);
  const key = `${col}|${frame}|${kq}`;
  let c = spriteCache.get(key);
  if (c) return c;
  if (spriteCache.size > 48) spriteCache.clear();
  c = document.createElement("canvas");
  c.width = Math.ceil(SPR_W * kq);
  c.height = Math.ceil(SPR_H * kq);
  const g = c.getContext("2d")!;
  g.scale(kq, kq);
  g.translate(PAD, PAD);
  const art = ART[frame % 2]!;
  const put = (fill: string, dx: number, dy: number, grow: number) => {
    g.fillStyle = fill;
    for (let r = 0; r < art.length; r++) {
      for (let cc = 0; cc < 8; cc++) {
        if (art[r]![cc] === "#") g.fillRect(cc * P + dx - grow, r * P + dy - grow, P + grow * 2, P + grow * 2);
      }
    }
  };
  const o = 0.85;                    // ink rim, a third of an art pixel
  put(C.ink, 1.2, 1.4, o);          // hard drop shadow
  put(C.ink, 0, 0, o);               // outline
  put(col, 0, 0, 0);                 // body
  // top sheen row
  g.fillStyle = "rgba(255,255,255,0.4)";
  for (let cc = 0; cc < 8; cc++) if (art[1]![cc] === "#") g.fillRect(cc * P, P, P, P * 0.6);
  spriteCache.set(key, c);
  return c;
}
